import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Badge from '../Badge';
import Checkbox from '../Checkbox';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';
import { positionKey } from '../../helpers/taxPlanner/totals';
import {
  Dash,
  ImpactValue,
  ResultValue,
  checkboxState,
  currencySymbol,
  formatMoney,
  formatQuantity,
  formatRate,
  formatUah,
  isoToDotted,
  signFlipped,
} from './PositionRow';

/**
 * One lot row of an expanded position. `sold` is the {sold, of} entry of the current fifoSelection (or undefined),
 * `resultUah` its sale result, `impact` the lot entry of selectImpact. Presentation only (T28 wires the handler).
 */
function LotRow({ position, lot, sold, resultUah, impact, rounding, onToggleSelect }) {
  const { t } = useTranslation();
  const k = (key, opts) => t(`taxPlanner.scr02.positions.${key}`, opts);
  const testId = `lot-row-${positionKey(position)}-${lot.fifoOrder}`;
  const noPrice = position.price === null;
  const state = !sold ? 'none' : sold.sold === sold.of ? 'all' : 'partial';
  const date = isoToDotted(lot.openDateTime);
  const rate = `${formatRate(lot.openExchangeRate)} ${jurisdiction.currencySymbol}/${currencySymbol(position.currency)}`;

  return (
    <tr data-testid={testId} className="border-t border-border align-middle text-xs">
      <td className={clsx('bg-surface-muted px-2 py-2', sold ? 'shadow-[inset_3px_0_0_rgb(var(--accent-solid))]' : 'shadow-[inset_3px_0_0_rgb(var(--border))]')}>
        <div className="flex justify-end">
          <Checkbox
            state={checkboxState(state)}
            disabled={noPrice}
            onChange={onToggleSelect}
            aria-label={k('selectLot', { n: lot.fifoOrder + 1, date })}
          />
        </div>
      </td>
      <td className="num bg-surface-muted py-2 pl-5 pr-2">
        <span className="text-text-muted">#{lot.fifoOrder + 1}</span> {date}
        {signFlipped(lot.unrealizedResult, lot.unrealizedResultUah) && (
          <>
            {' '}
            <Badge size="md" tone="muted">{k('badges.signFlip')}</Badge>
          </>
        )}
      </td>
      <td data-testid={`cell-${testId}-quantity`} className="bg-surface-muted px-2 py-2 text-right num">{formatQuantity(lot.quantity)}</td>
      <td data-testid={`cell-${testId}-price`} className="bg-surface-muted px-2 py-2 text-right num">
        {formatMoney(lot.openPrice, position.currency)} <span className="block text-text-muted text-xs">{rate}</span>
      </td>
      <td data-testid={`cell-${testId}-unrealized`} className="bg-surface-muted px-2 py-2 text-right">
        {lot.unrealizedResult == null ? <Dash /> : <ResultValue value={lot.unrealizedResult} formatted={formatMoney(lot.unrealizedResult, position.currency, { signed: true })} />}
      </td>
      <td data-testid={`cell-${testId}-unrealizedUah`} className="bg-surface-muted px-2 py-2 text-right">
        {lot.unrealizedResultUah == null ? <Dash /> : <ResultValue value={lot.unrealizedResultUah} formatted={formatUah(lot.unrealizedResultUah, rounding)} />}
      </td>
      <td data-testid={`cell-${testId}-sale`} className="bg-surface-muted px-2 py-2 text-right num">
        {state === 'all' && k('allLot', { count: formatQuantity(sold.of) })}
        {state === 'partial' && <Badge size="md" tone="warn">{k('partLot', { sold: formatQuantity(sold.sold), of: formatQuantity(sold.of) })}</Badge>}
        {state === 'none' && <Dash />}
      </td>
      <td data-testid={`cell-${testId}-saleResult`} className="bg-surface-muted px-2 py-2 text-right">
        {sold ? <ResultValue value={resultUah} formatted={formatUah(resultUah, rounding)} /> : <Dash />}
      </td>
      <td data-testid={`cell-${testId}-impact`} className="bg-surface-muted px-2 py-2 text-right">
        {sold && impact ? <ImpactValue value={impact.impact} rounding={rounding} /> : <Dash />}
      </td>
      <td className="bg-surface-muted px-2 py-2" />
    </tr>
  );
}

export default LotRow;
